/**
 * PublicCustomLayout — mounts an admin-authored record template.
 *
 * The rendered tree comes from renderRecordTemplate() as real DOM nodes, so it
 * is attached imperatively rather than through JSX. dangerouslySetInnerHTML is
 * NOT used and must not be introduced here: it would parse the string as
 * markup in the live document, discarding the sanitise-then-textContent
 * guarantees the pipeline exists to provide.
 *
 * The host node is emptied before each attach, so a re-render replaces the
 * previous tree instead of appending a second copy.
 *
 * `data` is keyed by table name — { event: record } for an `event` table,
 * { property: record } for a `property` table. Nothing here knows or cares
 * which application it is serving (spec D-8).
 *
 * `failed` is the set of related aliases that could not be loaded, so their
 * sections can say "unavailable" rather than "none" — see relatedScope.js.
 */

import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PublicActionForm from './PublicActionForm';
import { renderRecordTemplate } from '../../utils/renderRecordTemplate';
import { resolveActionButtons } from '../../utils/actionButtons';
// Shared with the React event detail page, so both surfaces answer "has this
// visitor already registered?" identically — see utils/actionCompletion.js.
import { completedMessage, decideCompleted } from '../../utils/actionCompletion';

const STYLE_CLASS = {
  filled: 'btn btn-primary',
  outlined: 'btn btn-outline-primary',
  text: 'btn btn-link',
};

/**
 * The inline result of a formless action, as a DOM node.
 *
 * Built imperatively because the buttons it accompanies are themselves built
 * into the rendered template rather than mounted as React children.
 *
 * `role="status"` with `aria-live` so the outcome reaches a screen reader: the
 * message appears without any navigation, which is otherwise silent.
 */
function buildFeedback(state) {
  const node = document.createElement('div');
  const busy = state.status === 'busy';
  const failedWrite = state.status === 'error';

  node.className = 'w-100 small mt-1 '
    + (failedWrite ? 'text-danger' : busy ? 'text-secondary' : 'text-success');
  node.setAttribute('role', 'status');
  node.setAttribute('aria-live', 'polite');
  node.setAttribute('data-arcee-action-state', state.status);
  node.textContent = busy ? 'Working…' : (state.message || (failedWrite ? 'Could not complete that.' : 'Done.'));

  return node;
}

/**
 * Where the confirmation is drawn, in order of precedence:
 *
 *   1. `<div data-arcee-completed="register">` — the builder chose the spot in
 *      their template, so the message can sit somewhere other than where the
 *      button was: a banner, a hero, beside the date.
 *   2. the button's form anchor, if the template has one
 *   3. the button's own place in the actions anchor
 *
 * Presentation belongs in the template; whether it happens at all stays in the
 * button's configuration.
 */
function buildCompleted(button) {
  const node = document.createElement('div');
  node.className = 'w-100 small text-success arcee-action-completed';
  node.setAttribute('role', 'status');
  // The same hook the feedback node carries, so a Page Builder template can
  // restyle this from its CSS section with no code change.
  node.setAttribute('data-arcee-action-state', 'completed');
  node.setAttribute('data-arcee-action-id', String(button.id));
  node.textContent = completedMessage(button);
  return node;
}

export default function PublicCustomLayout({
  html, data, failed, actionButtons, actionForms, placement = 'detail',
  recordAlias, onAction, onSubmitForm, formState, scopeId = 'arcee-custom',
  completedActions,
}) {
  // Keys the server says this visitor has already completed, as
  // `<recordId>/<buttonId>` within the current table. Undefined for every
  // caller that does not pass it, which is what keeps existing pages identical.
  const doneKeys = completedActions instanceof Set ? completedActions : null;

  // The record THIS layout is drawing. A list page mounts one layout per card
  // with one shared state, so a completed action must be matched against the
  // record in hand — never against the button id alone, or registering for one
  // event would blank the button on every other card.
  const recordId = recordAlias ? data?.[recordAlias]?.id : undefined;

  // Wrapped so a surprise here can never take the page down with it.
  const isCompleted = React.useCallback(
    (button) => decideCompleted(button, doneKeys, recordId),
    [doneKeys, recordId],
  );
  const hostRef = useRef(null);
  // Anchors discovered in the rendered markup, each paired with the hidden
  // values the template placed inside it. Kept in state so the forms mount as
  // real React children rather than being hand-built in the DOM.
  const [formAnchors, setFormAnchors] = useState([]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    // recordAlias names the record this layout draws, so a related block's
    // datetime can fall back to the zone that record declares.
    const rendered = renderRecordTemplate(html, data, failed, { recordAlias });
    host.replaceChildren(...Array.from(rendered.childNodes));

    // Buttons are built here rather than in the template so their destination
    // comes from stored config, not from markup. A redirect becomes a real
    // <a href>, so hover previews and middle-click behave normally and no JS is
    // needed to navigate; only a submit needs a handler.
    const buttons = resolveActionButtons(actionButtons, data, placement, recordAlias);
    if (buttons.length === 0) return undefined;

    const cleanups = [];

    // Builder-placed slots for the confirmation. Emptied first: a slot for a
    // button that is NOT completed must not leave its placeholder markup on
    // screen, and re-running this effect must not stack two copies.
    const completedAnchors = new Map();
    host.querySelectorAll('[data-arcee-completed]').forEach(node => {
      const id = node.getAttribute('data-arcee-completed');
      completedAnchors.set(id, node);
      node.replaceChildren();
      node.style.display = 'none';
    });

    // Which buttons the template also draws a FORM for. A completed button with
    // a form anchor is confirmed there; repeating it beside the button says the
    // same sentence twice on the same screen.
    const formAnchorIds = new Set(
      Array.from(host.querySelectorAll('[data-arcee-form]'))
        .map(node => node.getAttribute('data-arcee-form'))
    );

    host.querySelectorAll('[data-arcee-actions]').forEach(anchor => {
      anchor.replaceChildren();
      anchor.classList.add('d-flex', 'flex-wrap', 'gap-2', 'my-3');

      // An anchor may name a single button id, so a template with several
      // buttons can place each one where it belongs instead of receiving one
      // undifferentiated row. An empty value means "all of them".
      const only = anchor.getAttribute('data-arcee-actions');
      const forThisAnchor = only ? buttons.filter(b => b.id === only) : buttons;

      forThisAnchor.forEach(button => {
        // Already done on THIS record: the confirmation takes the button's
        // place, so there is nothing left to click and no pointless write.
        if (isCompleted(button)) {
          const placed = completedAnchors.get(String(button.id));
          if (placed) {
            placed.style.display = '';
            placed.appendChild(buildCompleted(button));
          } else if (!formAnchorIds.has(String(button.id))) {
            anchor.appendChild(buildCompleted(button));
          }
          return;
        }

        const el = document.createElement(button.type === 'redirect' ? 'a' : 'button');
        el.className = STYLE_CLASS[button.style] || STYLE_CLASS.filled;

        if (button.icon) {
          const icon = document.createElement('i');
          icon.className = `bi ${button.icon} me-1`;
          el.appendChild(icon);
        }
        el.appendChild(document.createTextNode(button.label));

        if (button.type === 'redirect') {
          el.setAttribute('href', button.href);
          if (button.openInNewTab) {
            el.setAttribute('target', '_blank');
            // Without noopener the opened page gets a handle back into this origin.
            el.setAttribute('rel', 'noopener noreferrer');
          }
        } else {
          el.setAttribute('type', 'button');
          const handler = event => {
            event.preventDefault();
            // The card may be wrapped in a link to its record. Without this the
            // click would submit the action AND navigate away from the result.
            event.stopPropagation();
            if (onAction) onAction(button);
          };
          el.addEventListener('click', handler);
          cleanups.push(() => el.removeEventListener('click', handler));
        }

        anchor.appendChild(el);
      });

      // Feedback belongs NEXT TO the button that caused it. A button with a
      // form gets this from PublicActionForm, but a formless button had no
      // reporting surface at all — its only feedback was a page-level banner
      // above the whole layout, which sits off-screen whenever the button is
      // below the fold. Clicking then looked like nothing happened, whether the
      // write succeeded or was refused.
      //
      // Built here, in the same effect that builds the buttons, because the
      // loop above starts with replaceChildren(): anything appended from a
      // separate effect is wiped the next time this one runs.
      // A reveal is shown in the page's modal, so the inline note beside the
      // button would repeat it and then outlive it.
      // Not when the button has already been withdrawn: its confirmation is
      // standing in that slot, so adding the success line underneath says the
      // same thing twice at the one moment the visitor is paying attention.
      const feedbackFor = formState
        ? forThisAnchor.find(b => b.id === formState.id)
        : null;
      if (formState && !formState.reveal && feedbackFor && !isCompleted(feedbackFor)) {
        anchor.appendChild(buildFeedback(formState));
      }
    });

    return () => cleanups.forEach(fn => fn());
  }, [html, data, failed, actionButtons, placement, recordAlias, onAction, formState, isCompleted]);

  // Locate the {{ actions.x }} / data-arcee-form anchors and read the hidden
  // values the template placed inside each one. Runs after the markup is
  // mounted, because the anchors only exist once the template has rendered.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    // Only forms whose button is actually available here. A button hidden by
    // its visibility rule (a closed course) must not show its form either —
    // otherwise a visitor fills it in and is refused by the server, which is a
    // worse experience than never being offered it.
    const available = new Set(
      resolveActionButtons(actionButtons, data, placement, recordAlias).map(b => b.id)
    );

    const found = [];
    host.querySelectorAll('[data-arcee-form]').forEach(node => {
      const buttonId = node.getAttribute('data-arcee-form');

      // HIDE rather than remove. This effect can run before the buttons have
      // loaded, and a removed node never comes back — the template is only
      // rebuilt when the markup or the record changes, so the form would stay
      // missing for the rest of the page's life. Hiding is reversible.
      if (!available.has(buttonId)) { node.style.display = 'none'; return; }
      node.style.display = '';
      const hidden = {};
      // Read the template's hidden values, then remove only THOSE nodes. The
      // author's own markup inside the anchor (a heading, an intro line) is
      // kept and the form is appended after it — clearing everything would
      // delete a "Register" heading that is meant to sit above the form, while
      // still leaving it behind when the whole anchor is dropped.
      node.querySelectorAll('[data-arcee-payload]').forEach(input => {
        hidden[input.getAttribute('data-arcee-payload')] = input.getAttribute('value') ?? '';
        input.remove();
      });
      found.push({ node, buttonId, hidden });
    });
    setFormAnchors(found);
  }, [html, data, failed, actionButtons, placement, recordAlias]);

  /**
   * Docked elements — `data-arcee-dock="scroll"`.
   *
   * A template marks an element it wants pinned to the edge of the viewport
   * and out of the way while the visitor is reading. This sets one attribute,
   * `data-arcee-dock-state`, to "here" or "away"; ALL presentation — where it
   * sits, how it leaves, whether it animates — stays in the template's own CSS.
   * The split matters: scroll DIRECTION is the one thing CSS cannot express,
   * because direction is the difference between two positions over time and a
   * stylesheet holds no memory of the previous frame. Everything else CSS can
   * already do, so nothing else is taken away from it.
   *
   * Nothing here names a button, a form or an application. Any template can
   * dock any element, exactly as `data-arcee-if` conditions any element.
   *
   * Reads only; never moves or restyles the node. A template that opts in but
   * writes no CSS for the state sees no change rather than a broken layout.
   */
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    const docks = Array.from(host.querySelectorAll('[data-arcee-dock="scroll"]'));
    if (docks.length === 0) return undefined;

    // Below this, a scroll is jitter or rubber-banding rather than intent —
    // without it the state flips continuously on iOS and the element flickers.
    const THRESHOLD_PX = 6;
    // Near the top the element is always offered: the visitor has not started
    // reading yet, so there is nothing to be out of the way of.
    const TOP_ZONE_PX = 24;

    const mark = (state) => {
      docks.forEach(node => node.setAttribute('data-arcee-dock-state', state));
    };
    mark('here');

    let last = window.scrollY;
    let frame = 0;

    const onScroll = () => {
      // Coalesced into a frame: scroll fires far faster than the screen paints,
      // and the work is a DOM write.
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const y = window.scrollY;
        const delta = y - last;
        if (y <= TOP_ZONE_PX) mark('here');
        else if (delta > THRESHOLD_PX) mark('away');
        else if (delta < -THRESHOLD_PX) mark('here');
        last = y;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [html, data, failed]);

  return (
    <>
      <div id={scopeId} className="arcee-custom-layout" ref={hostRef} />
      {formAnchors.map(({ node, buttonId, hidden }) => {
        const button = (actionButtons || []).find(b => b.id === buttonId);
        const formId = button?.action?.form_id;
        const form = formId ? (actionForms || {})[String(formId)] : null;
        if (!button || !form) return null;
        // The form is the other way in. Leaving it mounted would let a
        // registered visitor submit again, which is the same wasted write by a
        // different route.
        if (isCompleted(button)) {
          // A builder-placed slot has already been filled in the effect above;
          // repeating it in the form's place would say it twice.
          if (document.querySelector(`[data-arcee-completed="${buttonId}"]`)) return null;
          return createPortal(
            <div className="alert alert-success" role="status"
                 data-arcee-action-state="completed"
                 data-arcee-action-id={String(button.id)}>
              {completedMessage(button)}
            </div>,
            node,
            buttonId,
          );
        }
        return createPortal(
          <PublicActionForm
            button={button}
            form={form}
            hiddenValues={hidden}
            state={formState && formState.id === buttonId ? formState : null}
            onSubmit={onSubmitForm}
          />,
          node,
          buttonId,
        );
      })}
    </>
  );
}
